/* eslint-disable @faire/no-text-transform */
import { Core } from "@faire/design-tokens";
import {
  IDepartment,
  IJob,
} from "@faire/retailer-shared/greenhouse/greenhouseApi";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography, TypographyStyles } from "@faire/web/slate/Typography";
import styled, { css } from "styled-components";

import { DEPARTMENTS } from "@faire/retailer/views/careers/__internal__/common/departments";
import { extendedMediaStrings } from "@faire/retailer/views/careers/__internal__/common/media";

export const DESIGN_DEPARTMENT = "Design";

export const CUSTOMER_SUPPORT_DEPARTMENT = "Customer Support";

export const SALES_DEPARTMENT = "Sales";

export const horizontalPadding = css`
  padding-left: 32px;
  padding-right: 32px;

  ${responsiveMediaQueries.tablet} {
    padding-left: 72px;
    padding-right: 72px;
  }

  ${responsiveMediaQueries.desktop} {
    padding-left: 128px;
    padding-right: 128px;
  }

  ${extendedMediaStrings.xlarge} {
    padding-left: 208px;
    padding-right: 208px;
  }

  ${extendedMediaStrings.extraxlarge} {
    padding-left: 184px;
    padding-right: 184px;
  }
`;

const verticalPadding = css`
  padding-top: 48px;
  padding-bottom: 48px;

  ${responsiveMediaQueries.tablet} {
    padding-top: 72px;
    padding-bottom: 72px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-top: 128px;
    padding-bottom: 128px;
  }
`;

export const Section = styled(Column).attrs({ forwardedAs: "section" })<{
  $backgroundColor?: string;
  $withHorizontalPadding?: boolean;
  $withVerticalPadding?: boolean;
}>`
  position: relative;
  ${(props) =>
    props.$backgroundColor
      ? css`
          background-color: ${props.$backgroundColor};
        `
      : ""}

  ${(props) => (props.$withHorizontalPadding ? horizontalPadding : "")}
  ${(props) => (props.$withVerticalPadding ? verticalPadding : "")}
`;

export const LimitedSectionContent = styled(Column)`
  width: 100%;
  max-width: 1300px;
`;

export const EyebrowText = styled.label<{ $color?: string }>`
  ${({ theme }) => TypographyStyles({ theme })}
  font-size: 10px;
  line-height: 20px;
  letter-spacing: 2px;
  font-weight: 400;
  text-transform: uppercase;
  color: ${(props) => props.color ?? Core.text.subdued};

  ${responsiveMediaQueries.tabletAndAbove} {
    font-size: 13px;
    letter-spacing: 2.5px;
  }
`;

export const SectionHeadline = styled(Typography).attrs({
  variant: "displaySSerifRegular",
})`
  &&& {
    ${({ theme, color }) =>
      TypographyStyles({ variant: "displaySSerifRegular", theme, color })}

    ${responsiveMediaQueries.desktopAndAbove} {
      ${({ theme, color }) =>
        TypographyStyles({ variant: "displayMSerifRegular", theme, color })}
    }
  }
`;

export const DefinitionTerm = styled.dt`
  ${({ theme }) => TypographyStyles({ variant: "subheadingSansMedium", theme })}

  ${responsiveMediaQueries.desktopAndAbove} {
    ${({ theme }) =>
      TypographyStyles({ variant: "sectionHeaderSansMedium", theme })}
  }
`;

export const DefinitionDescription = styled.dd`
  ${({ theme }) => TypographyStyles({ as: "p", theme })}

  ${extendedMediaStrings.extraxlarge} {
    ${({ theme }) =>
      TypographyStyles({ variant: "subheadingSansMedium", theme })}
  }
`;

export const CTAButton = styled(Button)`
  width: fit-content;
  min-width: 220px;
`;

export const FadingImage = styled.img<{ $isActive: boolean }>`
  opacity: 0;
  transition: opacity 1s;
  ${({ $isActive }) => ($isActive ? "opacity: 1;" : "")}
`;

const LOCATION_OVERRIDE: Record<string, number> = {
  "San Francisco": 1,
  Kitchener: 2,
};

const ORDER_OVERRIDE: Record<string, number> = {
  "Product Design Manager": 1,
  "Product Design Lead": 2,
  "Product Designer": 3,
  "Brand Production Designer": 4,
  "Content Designer": 5,
  "Design Research Manager": 6,
};

export const mapDepartmentsWithTieredView = (
  data: IDepartment[],
  strictLocalize: StrictLocalizeFunction
): IDepartment[] => {
  const departments = data
    .filter((department) => department.jobs?.length)
    .map((department) => {
      const departmentInfo = DEPARTMENTS(strictLocalize)[department.name];
      department.title = departmentInfo?.title ?? department.name;
      department.parentDepartment = departmentInfo?.parentDepartment;
      department.childDepartments = [];
      department.jobs.map((job) => {
        if (Array.isArray(job.metadata)) {
          const typeMeta = job.metadata.find(
            (meta) => meta.name === "Employment Type"
          );
          if (typeMeta) {
            job.type = typeMeta.value;
          } else {
            job.type = "-";
          }
        }
      });
      if (department.name === DESIGN_DEPARTMENT) {
        department.jobs = department.jobs.sort((job1: IJob, job2: IJob) => {
          const locationPriority =
            (LOCATION_OVERRIDE[job1.location.name.trim()] ??
              Number.MAX_SAFE_INTEGER) -
            (LOCATION_OVERRIDE[job2.location.name.trim()] ??
              Number.MAX_SAFE_INTEGER);
          if (locationPriority !== 0) {
            return locationPriority;
          }
          return (
            (ORDER_OVERRIDE[job1.title.trim()] ?? Number.MAX_SAFE_INTEGER) -
            (ORDER_OVERRIDE[job2.title.trim()] ?? Number.MAX_SAFE_INTEGER)
          );
        });
      }
      if (department.parentDepartment === CUSTOMER_SUPPORT_DEPARTMENT) {
        return {
          name: "Customer Support",
          jobs: [],
          title: "Customer Support",
          childDepartments: [department],
        };
      }
      if (department.parentDepartment === SALES_DEPARTMENT) {
        return {
          name: "Sales",
          jobs: [],
          title: "Sales",
          childDepartments: [department],
        };
      }
      return department;
    });

  const departmentRecord: Record<string, IDepartment> = {};

  departments.forEach((newDepartment) => {
    const currentDepartment = departmentRecord[newDepartment.title];
    if (isNotUndefined(currentDepartment)) {
      departmentRecord[newDepartment.title] = {
        ...currentDepartment,
        childDepartments: [
          ...currentDepartment.childDepartments,
          ...newDepartment.childDepartments,
        ],
      };
    } else {
      departmentRecord[newDepartment.title] = { ...newDepartment };
    }
  });
  return Object.values(departmentRecord);
};

export const getJobOpeningCount = (departments: IDepartment[]) => {
  let count = 0;
  departments.forEach((department) => {
    count += department.jobs.length;
    if (department.childDepartments.length > 0) {
      department.childDepartments.forEach((childDepartments) => {
        count += childDepartments.jobs.length;
      });
    }
  });
  return count;
};

export const getFilteredDepartmentByLocation = (
  departments: IDepartment[],
  selectedLocation: string
): IDepartment[] =>
  departments
    .map((department) => {
      if (department.childDepartments.length > 0) {
        const filteredChildDepartments = getFilteredDepartmentByLocation(
          department.childDepartments,
          selectedLocation
        );
        if (filteredChildDepartments.length > 0) {
          return {
            ...department,
            childDepartments: filteredChildDepartments,
          };
        }
      }
      const isSelectedLocationDepartment = department.jobs.some(
        (job) => job.location.name === selectedLocation
      );
      if (isSelectedLocationDepartment) {
        const filteredJobs = department.jobs.filter(
          (job) => job.location.name === selectedLocation
        );
        return {
          ...department,
          jobs: filteredJobs,
        };
      }
      return undefined;
    })
    .filter(isNotUndefined);
